import { useCallback, useEffect, useRef, useState } from 'react';

const RETRY_DELAY_MS = 50;

export type UsePendingPublishOptions = {
  /** Reactive: a validation pass is running, so the click has to wait. */
  isBusy: boolean;
  /** The solution the click was made for; a change drops the pending click. */
  resetKey: unknown;
  /** Read at decision time, after the pass and its saves have settled. */
  canPublishNow: () => boolean;
  /** Non-reactive re-check of `isBusy`, read after the saves flushed. */
  isStillBusy: () => boolean;
  waitForSaves: () => Promise<void>;
  /** What a click on an idle, publishable button has always done. */
  onReady: () => void;
  /** The pass ended with the draft unpublishable — show why. */
  onBlocked: () => void;
};

/**
 * PSTUDIO-4242: holds a Publish click made while validation is running, and
 * acts on it once the pass resolves — `onReady` when the draft came back
 * publishable, `onBlocked` when it did not. Before this, the button was simply
 * disabled for the length of the pass, so a click inside that window (a
 * sidebar edit starts one in the same tick) was silently lost.
 *
 * It never widens WHEN publishing is allowed: the decision is `canPublishNow()`
 * read after the pass, the same gate an idle click is behind.
 *
 * "The pass resolved" is checked twice, because one sidebar commit starts two
 * passes: the second only begins once the DB save lands. So the settle step
 * first awaits pending saves, then yields a macrotask, then re-reads the store;
 * if a pass is running again it keeps waiting.
 */
export const usePendingPublish = (options: UsePendingPublishOptions) => {
  const { isBusy, resetKey } = options;

  const latest = useRef(options);
  latest.current = options;

  // Keyed by the solution it was made for, so a click can never be acted on
  // for a different solution than the one the user clicked in.
  const [pendingFor, setPendingFor] = useState<{ key: unknown } | null>(null);
  const isPending = pendingFor !== null && Object.is(pendingFor.key, resetKey);
  const isPendingRef = useRef(isPending);
  isPendingRef.current = isPending;

  // Bumped to re-run the settle step when the store reports a pass the
  // `isBusy` prop has not caught up with yet.
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    setPendingFor(null);
  }, [resetKey]);

  const request = useCallback(() => {
    const { current } = latest;
    if (isPendingRef.current) return;
    // The live store too: a commit sets its flag synchronously, and a click can
    // land before the observer has re-rendered `isBusy`.
    if (!current.isBusy && !current.isStillBusy()) {
      current.onReady();
      return;
    }
    isPendingRef.current = true;
    setPendingFor({ key: current.resetKey });
  }, []);

  useEffect(() => {
    if (!isPending || isBusy) return;

    let cancelled = false;
    void (async () => {
      try {
        await latest.current.waitForSaves();
      } catch {
        // Decide anyway: the publish modal awaits saves again before it
        // publishes, and it is the one that reports a failed save.
      }
      await new Promise((resolve) => setTimeout(resolve, 0));
      if (cancelled) return;

      const { current } = latest;
      if (current.isStillBusy()) {
        // Back off before re-checking, so a gap the prop has not caught up
        // with is not a macrotask busy-loop.
        await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
        if (!cancelled) setAttempt((n) => n + 1);
        return;
      }
      setPendingFor(null);
      if (current.canPublishNow()) {
        current.onReady();
      } else {
        current.onBlocked();
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isPending, isBusy, resetKey, attempt]);

  return { isPending, request };
};
